import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, map, Observable, tap } from "rxjs";
import { LoaderService } from "./loader.service";
import { ActionResult, ActionResultList, ADMINISTRATOR_ROLE_ID, FindUsersDetailsRequest, RoleEdit, UserDetail } from "@model";
import { EnvironmentService } from "./environment.service";

@Injectable({ providedIn: 'root' })
export class AdministratorsService {
  private _currentUserDetails = new BehaviorSubject<UserDetail | undefined>(undefined);

  get currentUserDetails(): BehaviorSubject<UserDetail | undefined> {
    return this._currentUserDetails;
  }

  constructor(private http: HttpClient, private loaderService: LoaderService, private environmentService: EnvironmentService) {}

  findAdministrators(showLoader = false): Observable<UserDetail[]> {
    if (showLoader) this.loaderService.startLoading();
    return this.http.get<ActionResultList<UserDetail[], number>>(this.environmentService.getApiUrl() + "/api/administrators/findadmins").pipe(
      tap(() => { if (showLoader) this.loaderService.dismissLoading(); }),
      map(value => value?.result?.length ? value.result : [])
    );
  }

  deleteAdministrator(userId: number): Observable<boolean> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/administrators/deleteadmin", userId).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value.result ?? false)
    );
  }

  findUsers(userFilter: FindUsersDetailsRequest, hideLoading?: boolean, onStartFetch?: () => void, onEndFetch?: () => void): Observable<UserDetail[]> {
    if (onStartFetch) onStartFetch();
    if (!hideLoading) this.loaderService.startLoading();
    return this.http.post<ActionResultList<UserDetail[], number>>(this.environmentService.getApiUrl() + "/api/administrators/getusersbyrole", userFilter).pipe(
      tap(() => {
        if (onEndFetch) onEndFetch();
        if (!hideLoading) this.loaderService.dismissLoading();
      }),
      map(value => value?.result?.length ? value.result : [])
    );
  }

  fetchUser(userId: number, hideLoading?: boolean): Observable<UserDetail | null> {
    if (!hideLoading) this.loaderService.startLoading();
    return this.http.post<ActionResult<UserDetail>>(this.environmentService.getApiUrl() + "/api/administrators/getuserdetailbyid", userId).pipe(
      tap(() => { if (!hideLoading) this.loaderService.dismissLoading(); }),
      map(value => value?.result ?? null)
    );
  }

  addNewOrganiserAdmin(userDetail: UserDetail): Observable<ActionResult<boolean>> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + `/api/administrators/addNewOrganiserAdmin`, userDetail).pipe(
      tap(() => this.loaderService.dismissLoading())
    );
  }

  updateOrganiserAdmin(userDetail: UserDetail): Observable<boolean> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + `/api/administrators/updateOrganiserAdmin`, userDetail).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value.result ?? false)
    );
  }

  addNewAdmin(userDetail: UserDetail): Observable<ActionResult<boolean>> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + `/api/administrators/addNewAdmin`, userDetail).pipe(
      tap(() => this.loaderService.dismissLoading())
    );
  }

  changeAdminUserRole(userId:number): Observable<boolean> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + '/api/administrators/changeUserRole', userId).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value.result ?? false)
    );
  }
}
